/**
 * All logic behind the Form Flow editor: loading a flow, seeding a brand-new one from its
 * template, every level / step / action mutation, the saveable-state rule, and both save paths
 * (update an existing flow, or save the in-flight flow as a new reusable one).
 *
 * Platform-agnostic on purpose: written to be consumed by `packages/mobile` as well as
 * `packages/web`, so it returns state, handlers and option lists only — never markup.
 */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import _ from "lodash";
import { formFlowController, getTemplates } from "@Actions/Forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { RootState } from "@Reducers/index";
import type { SpaceUserItem } from "@Reducers/projectUsers";
import type { FormFlowControllerRequest } from "@ServerTypes/forms/forms.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import {
  buildAddUserQuery,
  collectUsedSectionIds,
  createFlowStep,
  createIntermediateLevel,
  DEFAULT_ACTION_BUTTON_TEXTS,
  FORM_FLOW_MENU_ACTIONS,
  FORM_FLOW_STEP_TYPES,
  LEVEL_HEADING_MAX_LENGTH,
  getAddActionMenuItems,
  getAddStepMenuItems,
  getActionMenuItems,
  getFormFlowActionSteps,
  getInitialFormFlowLevels,
  getLevelMenuItems,
  getSectionsInUseForStep,
  getSelectableSections,
  getStepMenuItems,
  isFormFlowSaveable,
  stripLevelEditFlags,
  toFlowSection,
  toFlowUserFromFullName,
  toPersistableFormFlow,
} from "./formFlow.constants";
import type {
  FormFlowActionStep,
  FormFlowLevel,
  FormFlowModalState,
  FormFlowSection,
  FormFlowStep,
  FormFlowUser,
} from "./formFlow.types";
import { GetUsersListRequest } from "@ServerTypes/accounts/accounts.schema";

type FormUsersQuery = Partial<
  Pick<NonNullable<GetUsersListRequest["value"]>, "projectId" | "sectionId" | "selectionScope" | "formId" | "spaceId">
>;
/**
 * A template section as the flow editor handles it — the three members it actually reads, and no
 * more (`toFlowSection` in formFlow.constants.ts copies exactly `_id`, `sectionCode`,
 * `sectionHeading`).
 *
 * Deliberately NOT `FormTemplateField`: that is the template-DEFINITION shape, which requires
 * `progress` / `remDuration`. A stored instance's captured template
 * (`inspectionData.selectedForm.template`) has no such members, and this editor never touches
 * them, so requiring them here rejected a caller that is perfectly valid.
 *
 * `_id` stays required: `formTemplateFieldSchema` marks it optional, but the form builder always
 * mints a 36-char UUID per section and the whole flow layer keys sections by it (`toFlowSection`,
 * `getSelectableSections`, `collectUsedSectionIds`).
 */
type EditorTemplateSection = { _id: string; sectionCode?: string; sectionHeading: string };

/**
 * The Form template a flow is built on, as this editor uses it.
 *
 * Declared here because `getTemplatesResponse` types its payload as `result: z.array(z.any())`,
 * so there is no server type to import. Only the four fields the editor reads are named — do not
 * grow this into a second template type; the sections themselves are the server's
 * `FormTemplateField`.
 */
export interface FormFlowTemplate {
  _id?: string;
  name?: string;
  resourceType?: string;
  template?: EditorTemplateSection[];
}

/**
 * The flow document `formFlowController({ formFlowId })` returns.
 *
 * Declared here because `formFlowControllerResponse` types its payload as
 * `result: z.array(z.any())` — and the single-flow read does not even return an array, it returns
 * one document — so there is nothing to import. Every field is optional only so the pre-fetch
 * initial state (`{}`) fits; a persisted flow carries all of them.
 */
interface LoadedFormFlow {
  name?: string;
  space?: { _id?: string };
  /** `selectedFormSchema._id` is an `objectIdString`, so the brand is carried here. */
  selectedForm?: { _id?: ObjectIdString };
  formFlow?: FormFlowLevel[];
}

/**
 * Narrows a `getTemplates` payload to the template document.
 *
 * `getTemplatesResponse` declares `result: z.array(z.unknown())` while a by-id fetch returns the
 * document itself, so what arrives is untyped. This checks the wire's actual lie — a document, not
 * an array — and nothing more: every field `FormFlowTemplate` names is optional and every read of
 * one is already guarded (`updatedTemplate._id`, `updatedTemplate.template`).
 * SERVER SCHEMA GAP — reported.
 */
const isFormFlowTemplate = (value: unknown): value is FormFlowTemplate =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Narrows a `formFlowController({ formFlowId })` payload to the flow document.
 *
 * `formFlowControllerResponse` declares `result: z.array(z.unknown())`, and the single-flow read
 * does not return an array at all — it returns one document. Both of the things the editor then
 * walks are checked here rather than named by assertion: that it IS a document, and that it carries
 * its levels (previously a `.formFlow!` that would have thrown on a flow without them).
 * SERVER SCHEMA GAP — reported.
 */
const isLoadedFormFlow = (value: unknown): value is LoadedFormFlow & { formFlow: FormFlowLevel[] } =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  "formFlow" in value &&
  Array.isArray(value.formFlow);

/** What `InPlaceEditorComponent.actionSuccess` hands back; both commit handlers read it defensively. */
interface InPlaceEditorResult {
  value?: string | number;
}

/**
 * The `args` of a menu-driven mutation: either a web dropdown's select event, or the ad-hoc
 * payload the inline remove buttons build in `removeSection` / `removeUser`.
 */
interface FormFlowOptionSelectArgs {
  /**
   * Web `SelectDropDown` select event: the picked menu entry. `id` is optional because
   * `SelectEventArgs["item"]` declares it so.
   */
  item?: { id?: string };
  /** Supplied by `removeSection`. */
  sectionId?: string;
  /** Supplied by `removeUser`. */
  userId?: string;
}

interface FormFlowOptionSelectParams {
  /** Web menu event / inline-remove payload. Absent on mobile, which always passes `action`. */
  args?: FormFlowOptionSelectArgs;
  /** Passed explicitly by mobile and the inline remove buttons; otherwise read off `args.item.id`. */
  action?: string;
  levelIndex: number;
  stepIndex?: number;
  actionStep?: FormFlowActionStep;
}

/**
 * Payload of the two save paths. Both are assembled from editor state, and `formFlow` carries the
 * web `FormFlowLevel` shape — the server levels plus the UI-only edit flags and open index
 * signature the type still declares, even though `toPersistableFormFlow` has already stripped the
 * flags — so `formFlowController`'s `Exact<>` constraint cannot see it as an exact union member.
 * The request is therefore asserted at the call; the bytes on the wire are unchanged.
 */
interface FormFlowSavePayload {
  action: "save" | "update";
  _id?: string | null;
  data: {
    name?: string;
    selectedTemplate?: string;
    formCategory?: string;
    formFlow: FormFlowLevel[];
    projectId?: string;
    spaceId?: string;
  };
}

interface UseFormFlowCreationParams {
  /** Existing flow to load and update. `null` seeds a new flow from `selectedTemplate`. */
  formFlowId?: string | null;
  projectId?: string;
  /** Space the flow belongs to. Falls back to the loaded flow's own space. */
  spaceId?: string;
  /** Template a brand-new flow is seeded from. */
  selectedTemplate?: FormFlowTemplate;
  /** Which screen embeds the editor — see `FORM_FLOW_SOURCES`. */
  source?: string;
  /** Edit a single level in isolation and report it back through `onLevelChange`. */
  singleLevelEditMode?: boolean;
  /** Levels to seed from in single-level edit mode. */
  formFlow?: FormFlowLevel[] | null;
  /** Index of the level being edited in single-level edit mode. */
  editLevelIndex?: number | null;
  /** Called with the edited level (edit flags stripped) and whether it is saveable. */
  onLevelChange?: (level: FormFlowLevel, isSaveable: boolean) => void;
  /** Leaves the editor — also called after a successful save. */
  handleBackClick?: () => void;
}

function useFormFlowCreation({
  formFlowId = null,
  projectId,
  spaceId,
  selectedTemplate = {},
  source = "",
  singleLevelEditMode = false,
  formFlow = null,
  editLevelIndex = null,
  onLevelChange = () => {},
  handleBackClick = () => {},
}: UseFormFlowCreationParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spaceUsers = useSelector((state: RootState) => state.spaceUsers);

  const [formFlowLevels, setFormFlowLevels] = useState<FormFlowLevel[]>(() =>
    singleLevelEditMode && Array.isArray(formFlow)
      ? formFlow.map((level: FormFlowLevel) => ({
          ...level,
          headingEditMode: false,
          subBtnEditMode: false,
          rejectBtnEditMode: false,
          approveBtnEditMode: false,
          rollBackBtnEditMode: false,
        }))
      : [],
  );
  const [formFlowData, setFormFlowData] = useState<LoadedFormFlow>({});
  const [updatedTemplate, setUpdatedTemplate] = useState<FormFlowTemplate>(selectedTemplate);
  const [editMode, setEditMode] = useState(!!singleLevelEditMode);
  const [modalState, setModalState] = useState<FormFlowModalState>({
    modalName: "",
    levelIndex: null,
    stepIndex: null,
    currentUsers: [],
    currentSections: [],
  });
  const [allSelectedSections, setAllSelectedSections] = useState<string[]>([]);
  const [openNewFlowSaveModal, setOpenNewFlowSaveModal] = useState({ status: false, title: "" });
  // `undefined`, not `null`: this is forwarded straight to the add-user modal's optional
  // `customQuery` prop, and "no explicit query" is what an absent optional prop already means.
  const [addUserQuery, setAddUserQuery] = useState<FormUsersQuery | undefined>(undefined);

  /** Explicit space wins; otherwise the space of the loaded flow. */
  const resolvedSpaceId = spaceId || formFlowData.space?._id;

  useEffect(() => {
    if (resolvedSpaceId) {
      setAddUserQuery(buildAddUserQuery(resolvedSpaceId));
    }
  }, [resolvedSpaceId]);

  /** The four level actions, translated. */
  const actionSteps = getFormFlowActionSteps();

  /* ---------------- loading ---------------- */

  const fetchFormFlowData = useCallback(async () => {
    // `formFlowId` is `string | null` on the params while the request schema takes the branded id,
    // so it is PARSED rather than branded by assertion. The caller already guards on it being set;
    // this additionally keeps a malformed id from reaching the endpoint.
    const flowId = objectIdString.safeParse(formFlowId);
    if (!flowId.success) return;
    const res = await formFlowController({ formFlowId: flowId.data });
    const flow: unknown = res.data.result;
    if (res.data.status && isLoadedFormFlow(flow)) {
      const loadedFlow = flow.formFlow.map((level: FormFlowLevel) => ({
        ...level,
        rejectBtnEditMode: false,
        approveBtnEditMode: false,
        rollBackBtnEditMode: false,
        headingEditMode: false,
        subBtnEditMode: false,
      }));
      setFormFlowData(flow);
      setFormFlowLevels(loadedFlow);
    }
  }, [formFlowId]);

  /** Template the loaded flow is built on. */
  const selectedFormId = formFlowData.selectedForm?._id;

  const getUpdatedTemplate = useCallback(async () => {
    if (!selectedFormId) return;
    const data = await getTemplates({
      formId: selectedFormId,
      isArchived: false,
    });
    const template: unknown = data.data.result;
    if (data.data.status && isFormFlowTemplate(template)) {
      setUpdatedTemplate({ ...template });
    }
  }, [selectedFormId]);

  // Fetch the template once the loaded flow names one. Re-runs once it arrives (`updatedTemplate._id`
  // becomes set), which the guard then turns into a no-op — so this still fetches exactly once.
  useEffect(() => {
    if (!updatedTemplate._id && selectedFormId) {
      getUpdatedTemplate();
    }
  }, [updatedTemplate._id, selectedFormId, getUpdatedTemplate]);

  useEffect(() => {
    if (formFlowId) {
      fetchFormFlowData();
    }
  }, [formFlowId, fetchFormFlowData]);

  /**
   * Seeds the two default levels for a new flow: every template section is fillable in
   * Pre-Submission and verifiable in Post-submission, and every space user may act at both.
   */
  const setInitialData = () => {
    // `SpaceUserItem.id` is a plain `z.string()` on the accounts schema while the flow user factory
    // takes the branded `ObjectIdString`, so the id is PARSED rather than branded by assertion — an
    // account id that is not an ObjectId names no user and is dropped instead of being seeded onto
    // the flow. (The slice is registered in both hosts' root reducers and starts as `[]`, so there
    // is nothing to guard against here; an empty roster seeds an unmanned flow, which leaves Save
    // disabled and the disclaimer explaining why.)
    const usersData: FormFlowUser[] = spaceUsers.flatMap((user: SpaceUserItem) => {
      const userId = objectIdString.safeParse(user.id);
      return userId.success ? [toFlowUserFromFullName({ id: userId.data, fullName: user.fullName })] : [];
    });
    // Only ever reached once `updatedTemplate._id` is set (see the effect below), i.e. from a
    // fetched template, which always carries its sections. Erased at emit.
    const templateSections = updatedTemplate.template!.map((section: EditorTemplateSection) => toFlowSection(section));

    const [preSubmissionLevel, postSubmissionLevel] = getInitialFormFlowLevels();

    preSubmissionLevel.dataSource = [
      { type: FORM_FLOW_STEP_TYPES.FILL, sections: [...templateSections], users: [...usersData] },
    ];
    preSubmissionLevel.allowedUsersToSubmit = [...usersData];
    preSubmissionLevel.allowedUsersToReject = [...usersData];

    postSubmissionLevel.dataSource = [
      { type: FORM_FLOW_STEP_TYPES.VERIFY, sections: [...templateSections], users: [...usersData] },
    ];
    postSubmissionLevel.allowedUsersToReject = [...usersData];
    postSubmissionLevel.allowedUsersToApprove = [...usersData];
    postSubmissionLevel.allowedUsersToRollBack = [...usersData];

    setFormFlowLevels([preSubmissionLevel, postSubmissionLevel]);
  };

  // `setInitialData` closes over `spaceUsers`, which loads asynchronously and can be refetched at any
  // time. Seeding must happen only when a template first becomes available: re-seeding on a
  // space-users change would throw away whatever the user had already built. So the effect triggers
  // on the template and reaches the current seeder through a ref.
  const setInitialDataRef = useRef(setInitialData);
  setInitialDataRef.current = setInitialData;

  useEffect(() => {
    // In single-level edit mode `formFlowLevels` is seeded from the `formFlow` param, so skip
    // generating a fresh default flow (which would overwrite the level being edited).
    if (!formFlowId && updatedTemplate._id && !singleLevelEditMode) {
      setInitialDataRef.current();
    }
  }, [formFlowId, updatedTemplate._id, singleLevelEditMode]);

  /* ---------------- derived state ---------------- */

  // Which sections a Fill step and a Verify step cover, across every level. Derived rather than
  // mirrored into state so they can never lag a render behind `formFlowLevels`.
  const { fill: allSectionsUsedForFill, verify: allSectionsUsedForVerify } = useMemo(
    () => collectUsedSectionIds(formFlowLevels),
    [formFlowLevels],
  );

  /** Whether the flow may be saved: full Fill+Verify coverage, and every step and action manned. */
  const saveBtnState = useMemo(
    () =>
      isFormFlowSaveable({
        levels: formFlowLevels,
        templateSectionCount: updatedTemplate.template?.length,
        sectionsUsedForFill: allSectionsUsedForFill,
        sectionsUsedForVerify: allSectionsUsedForVerify,
      }),
    [formFlowLevels, updatedTemplate.template, allSectionsUsedForFill, allSectionsUsedForVerify],
  );

  /** Section ids the add-sections picker must hide for this step. */
  const getSectionsInUse = (step: FormFlowStep) =>
    getSectionsInUseForStep({
      step,
      sectionsUsedForFill: allSectionsUsedForFill,
      sectionsUsedForVerify: allSectionsUsedForVerify,
    });

  /** Sections the add-sections picker may offer for the step the modal was opened for. */
  const selectableSections = getSelectableSections({
    template: updatedTemplate.template ?? [],
    currentSections: modalState.currentSections,
    stepType: modalState.stepData?.type,
    sectionsUsedForFill: allSectionsUsedForFill,
  });

  /* ---------------- modals ---------------- */

  const closeAddModal = () => {
    setModalState({
      modalName: "",
      levelIndex: null,
      stepIndex: null,
      currentUsers: [],
      currentSections: [],
    });
  };

  /** Opens the add-sections picker for one step. */
  const openSectionsModal = ({
    stepData,
    levelIndex,
    stepIndex,
  }: {
    stepData: FormFlowStep;
    levelIndex: number;
    stepIndex: number;
  }) => {
    setModalState({
      modalName: "sections",
      levelIndex,
      stepIndex,
      currentUsers: [],
      currentSections: getSectionsInUse(stepData),
      stepData,
    });
  };

  /** Opens the add-users picker for one step. */
  const openStepUsersModal = ({
    stepData,
    levelIndex,
    stepIndex,
  }: {
    stepData: FormFlowStep;
    levelIndex: number;
    stepIndex: number;
  }) => {
    setModalState({
      modalName: "users",
      levelIndex,
      stepIndex,
      currentUsers: stepData.users,
      currentSections: [],
    });
  };

  /** Opens the add-users picker for one level action. */
  const openActionUsersModal = ({
    level,
    levelIndex,
    actionStep,
  }: {
    level: FormFlowLevel;
    levelIndex: number;
    actionStep: FormFlowActionStep;
  }) => {
    setModalState({
      modalName: "users",
      levelIndex,
      // An action belongs to a LEVEL, not to a step, so there is no step to name — `null` is the
      // same "no step" the initial state and `closeAddModal` write, and `handleAddUsersClick` takes
      // the `actionStep` branch, which never reads it. This opener used to omit the key and assert
      // the setter to the shape it wrote instead.
      stepIndex: null,
      actionStep,
      // The four keys `usersField` can name are all `.optional()` on the wire, so a persisted level
      // that never had users at this action carries none; the picker's `currentUsers` is a list.
      currentUsers: level[actionStep.usersField] ?? [],
      currentSections: [],
    });
  };

  /* ---------------- mutations ---------------- */

  /**
   * Writes the picked users onto the step (or the action) the modal was opened for.
   *
   * `modalState.levelIndex` / `.stepIndex` are `null` only while no modal is open, and every
   * handler below runs from an open modal, which is set with both. The `!`s say exactly that and
   * are erased at emit; the code already indexed unconditionally.
   */
  const handleAddUsersClick = ({
    allSelectedUsers,
  }: {
    // The add-user picker's own row shape: `id` is optional there because grid rows inherit
    // `GridRowBase`'s optional `id`, and `name` is `.nullable().optional()` on the wire.
    allSelectedUsers: Array<{ id?: ObjectIdString; fullName?: string | null }>;
  }) => {
    const allUsers: FormFlowUser[] = allSelectedUsers.map(
      (user: { id?: ObjectIdString; fullName?: string | null }) => ({
        // A picked row always carries the account id it was listed by; only the grid's base row type
        // makes it optional. Erased at emit — the code already read it unconditionally.
        id: user.id!,
        name: user.fullName,
      }),
    );
    const modifiedData: FormFlowLevel[] = _.cloneDeep(formFlowLevels);
    if (modalState.actionStep) {
      modifiedData[modalState.levelIndex!]![modalState.actionStep.usersField] = allUsers;
    } else {
      modifiedData[modalState.levelIndex!]!.dataSource[modalState.stepIndex!]!.users = allUsers;
    }
    setFormFlowLevels(modifiedData);
  };

  /** Toggles one section in the add-sections picker. */
  const handleSectionSelection = ({ isChecked, sectionId }: { isChecked: boolean; sectionId: string }) => {
    if (isChecked) {
      setAllSelectedSections((prev: string[]) => [...prev, sectionId]);
    } else {
      setAllSelectedSections((prev: string[]) => prev.filter((secId: string) => secId != sectionId));
    }
  };

  /** Appends the picked sections to the step the modal was opened for. */
  const handleAddSectionsClick = () => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    // Reached only from the sections modal, which cannot open without a loaded template.
    const sectionToAdd = updatedTemplate
      .template!.filter((section: EditorTemplateSection) => allSelectedSections.includes(section._id))
      .map((section: EditorTemplateSection) => toFlowSection(section));
    modifiedData[modalState.levelIndex!]!.dataSource[modalState.stepIndex!]!.sections = [
      ...modifiedData[modalState.levelIndex!]!.dataSource[modalState.stepIndex!]!.sections,
      ...sectionToAdd,
    ];
    setFormFlowLevels(modifiedData);
    setAllSelectedSections([]);
    closeAddModal();
  };

  /**
   * Commits a renamed action button.
   *
   * `levelIndex` always names a rendered level here and in the four handlers below, so the `!`s
   * are only telling `noUncheckedIndexedAccess` what the render already guarantees. `btnTxtField`
   * / `btnTxtEditMode` are runtime-chosen level keys, so they resolve through the level's open
   * index signature.
   */
  const saveNewBtnName = ({
    args,
    actionStep,
    levelIndex,
  }: {
    args?: InPlaceEditorResult;
    actionStep: FormFlowActionStep;
    levelIndex: number;
  }) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex]![actionStep.btnTxtEditMode] = false;
    // Same coercion as `saveNewHeaderName`: the in-place editor result is `string | number`.
    modifiedData[levelIndex]![actionStep.btnTxtField] = String(args?.value ?? "");
    setFormFlowLevels(modifiedData);
  };

  const cancelBtnNameChange = ({ actionStep, levelIndex }: { actionStep: FormFlowActionStep; levelIndex: number }) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex]![actionStep.btnTxtEditMode] = false;
    setFormFlowLevels(modifiedData);
  };

  /** Puts one action button caption into in-place edit mode. */
  const handleActionBtnEdit = ({ levelIndex, actionStep }: { levelIndex: number; actionStep: FormFlowActionStep }) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex]![actionStep.btnTxtEditMode] = true;
    setFormFlowLevels(modifiedData);
  };

  const handleEditHeaderCancelClick = (levelIndex: number) => {
    const data = _.cloneDeep(formFlowLevels);
    data[levelIndex] = { ...data[levelIndex]!, headingEditMode: false };
    setFormFlowLevels(() => data);
  };

  /** Commits a renamed level heading (capped at `LEVEL_HEADING_MAX_LENGTH`). */
  const saveNewHeaderName = ({ args, levelIndex }: { args?: InPlaceEditorResult; levelIndex: number }) => {
    const data = _.cloneDeep(formFlowLevels);
    data[levelIndex] = {
      ...data[levelIndex]!,
      heading: String(args?.value ?? "").substring(0, LEVEL_HEADING_MAX_LENGTH),
      headingEditMode: false,
    };
    setFormFlowLevels(() => data);
  };

  /** Inserts a new intermediate level. Defaults to just before the final level. */
  const handleAddLevelClick = (destinationIndex = formFlowLevels.length - 1) => {
    const currentLevels = _.cloneDeep(formFlowLevels);
    const newLevel = createIntermediateLevel(formFlowLevels.length);
    setFormFlowLevels(() => [
      ...currentLevels.slice(0, destinationIndex),
      newLevel,
      ...currentLevels.slice(destinationIndex),
    ]);
  };

  /** Toggles a level condition such as `allowDynamicUserConfig`. */
  const handleConditionChange = ({
    levelIndex,
    field,
    value,
  }: {
    levelIndex: number;
    field: string;
    value: boolean;
  }) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex]![field] = value;
    setFormFlowLevels(modifiedData);
  };

  /**
   * Every level / step / action mutation driven by a menu pick. `action` may be passed
   * explicitly (mobile, and the inline remove buttons) or read from a web menu event's
   * `args.item.id`.
   */
  const handleOptionSelect = ({ args, action, levelIndex, stepIndex, actionStep }: FormFlowOptionSelectParams) => {
    if (!action) {
      // This branch is only reached when no explicit `action` was passed, i.e. from a web menu
      // event, which always exists and carries the picked item. Erased at emit.
      action = args!.item!.id;
    }
    let modifiedData = _.cloneDeep(formFlowLevels);

    switch (action) {
      case FORM_FLOW_MENU_ACTIONS.DELETE_LEVEL: {
        modifiedData = modifiedData.filter((_level: FormFlowLevel, index: number) => index != levelIndex);
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DELETE_STEP: {
        modifiedData[levelIndex]!.dataSource = modifiedData[levelIndex]!.dataSource.filter(
          (_step: FormFlowStep, index: number) => index != stepIndex,
        );
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DELETE_ACTION: {
        delete modifiedData[levelIndex]![actionStep!.usersField];
        delete modifiedData[levelIndex]![actionStep!.btnTxtEditMode];
        delete modifiedData[levelIndex]![actionStep!.btnTxtField];
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_AFTER: {
        handleAddLevelClick(levelIndex + 1);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_BEFORE: {
        handleAddLevelClick(levelIndex);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.RENAME_LEVEL: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          headingEditMode: true,
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DUPLICATE_LEVEL: {
        // A duplicated level keeps its steps and users but starts with no sections: a section
        // may only be filled/verified at one level.
        const levelToAdd = _.cloneDeep(modifiedData[levelIndex]!);
        levelToAdd.dataSource = levelToAdd.dataSource.map((step: FormFlowStep) => {
          step.sections = [];
          return step;
        });
        setFormFlowLevels(() => [
          ...modifiedData.slice(0, levelIndex + 1),
          levelToAdd,
          ...modifiedData.slice(levelIndex + 1),
        ]);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_FILL_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          dataSource: [...modifiedData[levelIndex]!.dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.FILL)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_MODIFY_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          dataSource: [...modifiedData[levelIndex]!.dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.MODIFY)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_VERIFY_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          dataSource: [...modifiedData[levelIndex]!.dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.VERIFY)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_MOVE_BACKWARD: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          rollBackBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.ROLL_BACK,
          rollBackBtnEditMode: false,
          allowedUsersToRollBack: [],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_REJECT_FORM: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex]!,
          rejectBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.REJECT,
          rejectBtnEditMode: false,
          allowedUsersToReject: [],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.REMOVE_SECTION: {
        modifiedData[levelIndex]!.dataSource[stepIndex!]!.sections = modifiedData[levelIndex]!.dataSource[
          stepIndex!
        ]!.sections.filter((section: FormFlowSection) => section._id != args?.sectionId);
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.REMOVE_USER: {
        if (actionStep) {
          modifiedData[levelIndex]![actionStep.usersField] = (
            modifiedData[levelIndex]![actionStep.usersField] ?? []
          ).filter((user: FormFlowUser) => user.id != args?.userId);
        } else {
          modifiedData[levelIndex]!.dataSource[stepIndex!]!.users = modifiedData[levelIndex]!.dataSource[
            stepIndex!
          ]!.users.filter((user: FormFlowUser) => user.id != args?.userId);
        }
        setFormFlowLevels(modifiedData);
        break;
      }
    }
  };

  /** Removes one section from a step. */
  const removeSection = ({
    sectionId,
    levelIndex,
    stepIndex,
  }: {
    sectionId: string;
    levelIndex: number;
    stepIndex: number;
  }) =>
    handleOptionSelect({ args: { sectionId }, levelIndex, stepIndex, action: FORM_FLOW_MENU_ACTIONS.REMOVE_SECTION });

  /** Removes one user from a step, or from a level action when `actionStep` is given. */
  const removeUser = ({
    userId,
    levelIndex,
    stepIndex,
    actionStep,
  }: {
    userId: string;
    levelIndex: number;
    stepIndex?: number;
    actionStep?: FormFlowActionStep;
  }) =>
    handleOptionSelect({
      args: { userId },
      levelIndex,
      stepIndex,
      actionStep,
      action: FORM_FLOW_MENU_ACTIONS.REMOVE_USER,
    });

  /* ---------------- saving ---------------- */

  /** Persists the edited levels onto the existing flow. */
  const handleSave = async () => {
    const data: FormFlowSavePayload = {
      _id: formFlowId,
      data: {
        formFlow: toPersistableFormFlow(formFlowLevels),
        projectId,
      },
      action: "update" as const,
    };
    const res = await formFlowController(data as FormFlowControllerRequest);
    const body = res.data;
    // `=== false`, not a truthiness else-branch: mobile type-checks this shared file without
    // strictNullChecks, where a falsy `status` cannot exclude the success member (its literal-`true`
    // status still admits null/undefined there) — only the direct literal comparison narrows.
    if (body.status === false) {
      dispatch(
        showToastNotification({
          content: untranslated(body.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      handleBackClick();
      dispatch(
        showToastNotification({
          content: t('Form flow "{{name}}" saved successfully', { name: formFlowData.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  };

  const handleCloseModalClick = () => {
    setOpenNewFlowSaveModal({ status: false, title: "" });
  };

  /** Saves the in-flight flow as a new reusable flow (the "Save Form Flow" dialog). */
  const handleSaveFlowClick = async () => {
    if (!resolvedSpaceId) {
      dispatch(showToastNotification({ content: t("Please select a space"), type: TOAST_TYPE_ERROR }));
      return;
    }
    const data: FormFlowSavePayload = {
      data: {
        name: openNewFlowSaveModal.title,
        selectedTemplate: updatedTemplate._id,
        formCategory: updatedTemplate.resourceType,
        formFlow: toPersistableFormFlow(formFlowLevels),
        projectId,
        spaceId: resolvedSpaceId,
      },
      action: "save" as const,
    };
    const res = await formFlowController(data as FormFlowControllerRequest);
    const body = res.data;
    // Same `=== false` narrowing as `handleSave` (see the comment there).
    if (body.status === false) {
      dispatch(
        showToastNotification({
          // Server error text is not a translation key, hence `untranslated()`. It is optional on
          // the failure branch, so fall back to the house message instead of rendering "undefined"
          // when the server fails without a reason — same shape as `handleSave` above.
          content: untranslated(body.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t('New Form Flow "{{name}}" has been created successfully', { name: data.data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      handleCloseModalClick();
    }
  };

  /** Discards edits: reloads a saved flow, or re-seeds a new one from its template. */
  const handleCancelEditClick = async () => {
    if (formFlowId) {
      await fetchFormFlowData();
    } else {
      setInitialData();
    }
    setEditMode(false);
  };

  const openSaveFlowModal = () => setOpenNewFlowSaveModal({ status: true, title: "" });

  /** Sets the name in the "Save Form Flow" dialog. Value-based — use this on mobile. */
  const setFlowName = (name: string) => {
    setOpenNewFlowSaveModal((prev) => ({ ...prev, title: name }));
  };

  /** Adapter for web inputs that emit a `{ target: { value } }` change event. */
  const handleFlowNameChange = (args: { target: { value: string } }) => {
    setFlowName(args.target.value);
  };

  /* ---------------- single-level edit mode ---------------- */

  // `onLevelChange` is passed as an inline arrow by its callers, so its identity changes on every
  // parent render. Reporting is driven by the edited level's content, not by the parent re-rendering,
  // so the effect triggers on the level and reaches the current callback through a ref.
  const onLevelChangeRef = useRef(onLevelChange);
  onLevelChangeRef.current = onLevelChange;

  /** The level being edited in single-level edit mode. */
  const editedLevel: FormFlowLevel | null | undefined =
    singleLevelEditMode && editLevelIndex != null ? formFlowLevels[editLevelIndex] : null;

  // Reporting follows the edited level, not the whole `formFlowLevels` array. Every mutation above
  // rebuilds the array with `_.cloneDeep`, so the edited level gets a fresh identity on each edit
  // and this still fires exactly when it used to.
  useEffect(() => {
    if (!editedLevel) return;
    onLevelChangeRef.current(stripLevelEditFlags(editedLevel), saveBtnState);
  }, [editedLevel, saveBtnState]);

  /* ---------------- option lists ---------------- */

  /** Actions this level actually has, in canonical order. */
  const getLevelActionSteps = (level: FormFlowLevel): FormFlowActionStep[] =>
    actionSteps.filter((step) => !!Object.hasOwn(level, step.btnTxtField));

  const levelMenuItems = (levelIndex: number) => getLevelMenuItems({ levelIndex, totalLevels: formFlowLevels.length });
  const addStepMenuItems = (levelIndex: number) =>
    getAddStepMenuItems({ levelIndex, totalLevels: formFlowLevels.length });
  const addActionMenuItems = ({ level, levelIndex }: { level: FormFlowLevel; levelIndex: number }) =>
    getAddActionMenuItems({ level, levelIndex });

  return {
    // core state
    formFlowLevels,
    setFormFlowLevels,
    formFlowData,
    updatedTemplate,
    editMode,
    setEditMode,
    saveBtnState,
    source,
    resolvedSpaceId,
    addUserQuery,

    // derived
    allSectionsUsedForFill,
    allSectionsUsedForVerify,
    getSectionsInUse,
    selectableSections,
    editedLevel,
    getLevelActionSteps,

    // option lists
    actionSteps,
    levelMenuItems,
    addStepMenuItems,
    addActionMenuItems,
    stepMenuItems: getStepMenuItems(),
    actionMenuItems: getActionMenuItems(),

    // modals
    modalState,
    setModalState,
    closeAddModal,
    openSectionsModal,
    openStepUsersModal,
    openActionUsersModal,
    allSelectedSections,
    handleSectionSelection,
    openNewFlowSaveModal,
    setOpenNewFlowSaveModal,
    openSaveFlowModal,
    handleCloseModalClick,
    setFlowName,
    handleFlowNameChange,

    // mutations
    handleOptionSelect,
    handleAddUsersClick,
    handleAddSectionsClick,
    handleAddLevelClick,
    handleConditionChange,
    handleActionBtnEdit,
    saveNewBtnName,
    cancelBtnNameChange,
    saveNewHeaderName,
    handleEditHeaderCancelClick,
    removeSection,
    removeUser,

    // persistence
    handleSave,
    handleSaveFlowClick,
    handleCancelEditClick,
    fetchFormFlowData,
    setInitialData,
  };
}

export default useFormFlowCreation;
